import type { TextInputProps } from '@mantine/core';
import { Input, useComputedColorScheme } from '@mantine/core';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { forwardRef, useState } from 'react';
import type { ExecutorVar } from '~/common/vm/protocol';
import { addAllLibTypes, addVarsType, setupTypescript } from '~/logic/monaco/setup-typescript';
import { setupMonacoThemes } from '~/logic/monaco/setup-theme';

const Editor = dynamic(() => import('@monaco-editor/react'), { ssr: false });

const ROW_HEIGHT = 24;

export const MonacoInputArea = forwardRef<
  HTMLDivElement,
  TextInputProps & { readonlyMessage?: string; variables?: ExecutorVar[]; rows?: number }
>(
  (
    {
      className,
      label,
      labelProps,
      inputWrapperOrder,
      autoFocus,
      value,
      disabled,
      defaultValue,
      error,
      onChange,
      onFocus,
      onBlur,
      readonlyMessage,
      variables,
      rows = 3,
    },
    ref,
  ) => {
    const colorScheme = useComputedColorScheme();
    const [focused, setFocused] = useState(autoFocus);

    const height = rows * ROW_HEIGHT;

    return (
      <Input.Wrapper
        label={label}
        labelProps={labelProps}
        error={error}
        inputWrapperOrder={inputWrapperOrder}
        className={className}
      >
        <div
          ref={ref}
          className={clsx('rounded-sm border-[1px]', focused ? `border-[#1b64a7]` : `border-[#424242]`)}
        >
          <Editor
            height={height}
            defaultLanguage="typescript"
            defaultValue={defaultValue !== undefined ? `${defaultValue}` : undefined}
            value={value !== undefined ? `${value}` : undefined}
            theme={colorScheme === 'dark' ? 'mantine-dark' : 'light'}
            beforeMount={async (monaco) => {
              setupMonacoThemes(monaco);
              setupTypescript(monaco);
              await addAllLibTypes(monaco, { playwrightTypeSelector: { pageLocators: true } });
              if (variables) {
                addVarsType(monaco, variables);
              }
            }}
            onMount={(editor) => {
              if (autoFocus) {
                editor.focus();
                editor.setPosition({ lineNumber: 1, column: 1 });
              }

              editor.onDidFocusEditorText(() => {
                const editorValue = editor.getValue();
                onFocus?.({
                  currentTarget: { value: editorValue },
                  target: { value: editorValue },
                } as React.FocusEvent<HTMLInputElement>);
                setFocused(true);
              });
              editor.onDidBlurEditorText(() => {
                const editorValue = editor.getValue();
                onBlur?.({
                  currentTarget: { value: editorValue },
                  target: { value: editorValue },
                } as React.FocusEvent<HTMLInputElement>);
                setFocused(false);
              });
            }}
            onChange={(value) => {
              // This is hacky, but it works (not sure why)
              onChange?.(value as unknown as React.ChangeEvent<HTMLInputElement>);
            }}
            options={{
              tabFocusMode: false,
              readOnly: disabled,
              readOnlyMessage: readonlyMessage ? { value: readonlyMessage } : undefined,
              scrollbar: {
                vertical: 'auto',
                horizontal: 'auto',
                useShadows: true,
                verticalScrollbarSize: 7,
              },
              padding: { top: 6, bottom: 6 },
              overviewRulerLanes: 0,
              lineNumbers: 'off',
              minimap: { enabled: false },
              fontSize: 13,
              scrollBeyondLastLine: false,
              wordWrap: 'on',

              lineNumbersMinChars: 0,
              glyphMargin: false,
              folding: false,
              renderLineHighlight: 'none',
              contextmenu: false,
              snippetSuggestions: 'none',
              linkedEditing: false,

              formatOnType: false,
              formatOnPaste: true,
              selectionHighlight: true,
            }}
            className="mantine-Input-input"
          />
        </div>
      </Input.Wrapper>
    );
  },
);

MonacoInputArea.displayName = 'MonacoInputArea';
